/**
 * 입구 종합 알림판·단면 유도 전광판 시나리오 (여유/혼잡/만차).
 *
 * 판정은 parking_induce common.ParkingGuideLevel 과 같아야 한다 — 다르면 QA 가 누른 버튼과
 * 현장 표시가 어긋난다. 정수 백분율(내림) 기준: 60 미만 여유, 61~99 혼잡, 100 이상 만차.
 * 60 은 원본 코드의 경계(> 60) 때문에 여유로 나온다.
 */
import type { QaSpace } from '../../services/SiteParkingQaService';

export type GuideLevel = 'smooth' | 'congested' | 'full';

export const GUIDE_LABEL: Record<GuideLevel, string> = {
  smooth: '여유',
  congested: '혼잡',
  full: '만차',
};
export const GUIDE_COLOR: Record<GuideLevel, string> = {
  smooth: '#22c55e',
  congested: '#f59e0b',
  full: '#ef4444',
};

export function guideLevel(used: number, total: number): GuideLevel {
  if (total === 0) return 'smooth';
  const pct = Math.floor((used * 100) / total);
  if (pct < 60) return 'smooth';
  if (pct > 60 && pct < 100) return 'congested';
  if (pct >= 100) return 'full';
  return 'smooth';
}

/** 입구 종합 알림판이 세는 주차면 — CCTV 에 연결된 일반·가족 면 (induce EntranceParkingInfo). */
export const ENTRANCE_TYPES = new Set(['REGULAR', 'FAMILY']);
export const countsForEntrance = (s: QaSpace) => !!s.cctv && ENTRANCE_TYPES.has(s.type);

/**
 * total 면 중 몇 면을 점유로 둘지. 여유는 30%, 혼잡은 80%, 만차는 전부 — 경계에서 멀리 둬서
 * 한두 면이 카메라로 바뀌어도 판정이 그대로다. 그 수로 원하는 판정이 안 나오면(면이 너무 적음)
 * 가까운 수를 찾고, 그래도 없으면 null (예: 2면은 혼잡을 만들 수 없다).
 */
export function targetOccupied(total: number, level: GuideLevel): number | null {
  if (total === 0) return null;
  const preferred = {
    smooth: Math.floor(total * 0.3),
    congested: Math.round(total * 0.8),
    full: total,
  }[level];
  const candidates = Array.from({ length: total + 1 }, (_, n) => n).sort(
    (a, b) => Math.abs(a - preferred) - Math.abs(b - preferred),
  );
  const n = candidates.find((c) => guideLevel(c, total) === level);
  return n ?? null;
}

const natural = new Intl.Collator('ko', { numeric: true });

/**
 * spaces 를 level 판정이 나오게 나눈다 — 이름 순으로 앞에서 n 면 점유, 나머지 빈자리.
 * 바뀌는 면만이 아니라 전부 보낸다: 보낸 면의 CCTV 만 카메라 반영이 멈추므로, 안 보낸 면이
 * 카메라 값으로 바뀌어 판정이 흔들리지 않게.
 */
export function planScenario(
  spaces: QaSpace[],
  level: GuideLevel,
): { occupy: number[]; vacate: number[] } | null {
  const n = targetOccupied(spaces.length, level);
  if (n === null) return null;
  const ordered = [...spaces].sort((a, b) => natural.compare(a.name, b.name));
  return {
    occupy: ordered.slice(0, n).map((s) => s.id),
    vacate: ordered.slice(n).map((s) => s.id),
  };
}

/** 단면 유도 전광판 방향 표기. */
export function directionLabel(direction: string): string {
  const d = direction.toUpperCase();
  if (d === 'LEFT') return '왼쪽';
  if (d === 'RIGHT') return '오른쪽';
  if (d === 'STRAIGHT') return '직진';
  return direction || '방향 없음';
}
